import { useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";
import type { PlaidLinkOnSuccessMetadata } from "react-plaid-link";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import { queryKeys } from "~/v2/api/queryKeys";
import { getPlaidAuthStatus } from "~/v2/api/rest/plaid";
import {
  createBankRelationship,
  type MutateBankRelationshipParams,
  verifyBankRelationship,
} from "~/v2/api/rest/relationships";
import type { AchRelationshipWithBankInfo } from "../../common/types";
import type { PlaidStatus } from "./PlaidStatusPanel.pres";
import {
  capturePlaidError,
  trackAuthStatusFailed,
  trackDuplicateSuccessSuppressed,
  trackLinkValidationFailed,
  trackPlaidEvent,
  trackPlaidFinished,
  trackRelationshipFailed,
} from "./plaidTelemetry";

export interface UseSubmitPlaidRelationshipArgs {
  accountID: string;
  /**
   * Getter (not a value) for the current ACH relationship. usePlaidLink captures
   * onSuccess at init and won't see later renders, so the handler must read the
   * relationship through a ref-backed getter to decide verify-vs-create.
   */
  getCurrentRelationship: () => AchRelationshipWithBankInfo | undefined;
  onStatusChange: (status: PlaidStatus) => void;
  /** Drop the persisted OAuth resume session (spent token / terminal cleanup). */
  clearSession: () => void;
  /** Run after a confirmed success (navigation is the caller's concern). */
  onCompleted: () => void;
}

/**
 * Owns the Plaid money path: idempotency latch, metadata validation,
 * verification-status resolution, create/verify POST, cache invalidation, and
 * the success handoff. Shared by the fresh-link and OAuth-resume pages so the
 * single POST-once guarantee lives in exactly one place (PRIME-698).
 */
export const useSubmitPlaidRelationship = ({
  accountID,
  getCurrentRelationship,
  onStatusChange,
  clearSession,
  onCompleted,
}: UseSubmitPlaidRelationshipArgs) => {
  const queryClient = useQueryClient();

  // One-shot latch so a duplicate onSuccess is a no-op and never double-POSTs.
  const hasSubmittedRelationshipRef = useRef(false);

  const handlePlaidSuccess = async (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    // Idempotency: onSuccess can fire more than once for a single link.
    // How a user reaches a duplicate call:
    //  - The Plaid hosted SDK (link-initialize.js) can be re-embedded on a
    //    remount, replaying onSuccess/onEvent.
    //  - OAuth resume on mobile: the page reloads on the redirect back and
    //    Link re-initializes, which can deliver onSuccess again.
    //  - Plaid can fire onSuccess twice in quick succession on a single link.
    // The latch is armed SYNCHRONOUSLY here (before any await) so a duplicate
    // loses the race and bails at the top, never reaching a second
    // POST /relationships (money-flow hazard). It MUST bail before
    // setStatus/telemetry: otherwise the re-entry sets "processing" and could
    // flip the UI to "error" even though the link succeeded. We release the
    // latch only on pre-POST validation failures (below) so a genuinely bad
    // first onSuccess doesn't permanently block a good retry.
    if (hasSubmittedRelationshipRef.current) {
      trackDuplicateSuccessSuppressed({
        link_session_id: metadata?.link_session_id,
      });
      return;
    }
    hasSubmittedRelationshipRef.current = true;

    const linkSessionId = metadata?.link_session_id;
    onStatusChange("processing");

    trackPlaidEvent({
      event: "LINK_SUCCESS_RECEIVED",
      link_session_id: linkSessionId,
      accounts_count: metadata?.accounts?.length ?? 0,
      institution_name: metadata?.institution?.name,
    });

    // Step 1: Validate metadata
    if (!metadata?.accounts?.length) {
      const reason = !metadata ? "metadata_missing" : "empty_accounts";

      trackLinkValidationFailed({ reason, link_session_id: linkSessionId });
      capturePlaidError(accountID, `Plaid Link validation failed: ${reason}`, {
        metadata,
        linkSessionId,
      });
      toastError(
        "Plaid encountered an error: unable to retrieve account information",
      );
      // No POST happened - release the latch so a retry can proceed.
      hasSubmittedRelationshipRef.current = false;
      onStatusChange("error");
      return;
    }

    const plaidAccount = metadata.accounts.at(0);

    if (!plaidAccount?.id) {
      trackLinkValidationFailed({
        reason: "missing_account_id",
        link_session_id: linkSessionId,
      });
      capturePlaidError(accountID, "Plaid account ID is missing", {
        metadata,
        linkSessionId,
      });
      toastError("Plaid encountered an error: account ID is missing");
      // No POST happened - release the latch so a retry can proceed.
      hasSubmittedRelationshipRef.current = false;
      onStatusChange("error");
      return;
    }

    // Step 2: Resolve verification status
    const currentRelationship = getCurrentRelationship();
    let verificationStatus = plaidAccount.verification_status ?? "";

    if (!verificationStatus && currentRelationship?.id) {
      try {
        const authInfo = await queryClient.fetchQuery({
          queryKey: queryKeys.funding.plaidAuth(plaidAccount.id),
          queryFn: () => getPlaidAuthStatus(plaidAccount.id),
        });
        verificationStatus = authInfo?.verification_status ?? "";

        trackPlaidEvent({
          event: "AUTH_STATUS_CHECKED",
          verification_status: verificationStatus,
          plaid_account_id: plaidAccount.id,
        });
      } catch (error) {
        trackAuthStatusFailed({ plaid_account_id: plaidAccount.id });
        capturePlaidError(accountID, "Failed to fetch Plaid auth status", {
          error,
          metadata,
          linkSessionId,
        });
        // Don't block — proceed with empty verificationStatus.
        // The backend will determine the appropriate auth method.
      }
    }

    // Step 3: Create or verify relationship
    const plaidPayload: MutateBankRelationshipParams = {
      plaid: {
        public_token: publicToken,
        account_id: plaidAccount.id,
        name: plaidAccount.name,
        mask: plaidAccount.mask,
        verification_status: verificationStatus,
      },
    };

    try {
      if (currentRelationship?.id) {
        await verifyBankRelationship({
          accountID,
          relationshipID: currentRelationship.id,
          body: plaidPayload,
        });

        trackPlaidEvent({
          event: "RELATIONSHIP_VERIFIED",
          verification_status: verificationStatus,
          relationship_id: currentRelationship.id,
        });
      } else {
        await createBankRelationship(accountID, plaidPayload);

        trackPlaidEvent({
          event: "RELATIONSHIP_CREATED",
          verification_status: verificationStatus,
          account_id: plaidAccount.id,
        });
      }
    } catch (error) {
      trackRelationshipFailed({
        error_message: error instanceof Error ? error.message : String(error),
        verification_status: verificationStatus,
      });
      capturePlaidError(
        accountID,
        "Failed to create or verify bank relationship",
        { error, metadata, linkSessionId },
      );
      toastError("Plaid encountered an error: failed to link bank account");
      // The persisted token is spent now; drop it so a retry fetches a fresh
      // one instead of replaying a dead OAuth session. Latch stays armed: we
      // don't know if the POST partially succeeded, so no auto-retry.
      clearSession();
      onStatusChange("error");
      return;
    }

    // Step 4: Success. Treat the POST as terminal FIRST — mark completed and
    // clear the spent session before anything that can throw. invalidateQueries
    // runs as best-effort AFTER, so a throw there can never flip an
    // already-succeeded link back to "error", and there is no finally block to
    // clobber the completed status.
    clearSession();
    onStatusChange("completed");
    toastSuccess("Account linked successfully!");

    trackPlaidFinished({
      event: "FINISHED",
      verification_status: verificationStatus,
      account_id: plaidAccount.id,
    });

    onCompleted();

    try {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.funding.allRelationships.all(),
      });
    } catch (error) {
      // Non-fatal: the link already succeeded. Record it but keep "completed".
      capturePlaidError(
        accountID,
        "Failed to invalidate relationships cache after successful link",
        { error, linkSessionId },
      );
    }
  };

  return { handlePlaidSuccess };
};
